import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import Flex from '@wix/da-ds/pkg/Flex';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { PapiGallection } from '@wix/da-papi-types';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import { GALLERY_SUBFOLDERS_STREAM } from '../../../../constants';
import FolderDescription from '../../FolderDescription';
import { FolderListDesktop } from '../FolderList';

import s from './FolderHeaderDesktop.scss';

export interface Props {
  selectedFolder: PapiGallection;
  subfolders?: PapiGallection[];
  hasDeviations?: boolean;
  activeQuery?: string;
}

const FolderHeaderDesktop: FC<Props> = ({
  selectedFolder,
  subfolders,
  hasDeviations,
  activeQuery,
}) => {
  const { t } = useTranslation();
  const foldersExpandedByDefault = !hasDeviations;
  const hasSubfolders = subfolders && subfolders.length > 0;
  // When searching we remove the subfolders
  const showSubFolders = activeQuery && activeQuery !== '' ? false : true;
  const { isPremium, isPaidFolder, isWatchToAccess } = normalizePremiumFolderData(
    selectedFolder?.premiumFolderData
  );
  const isSpecialFolder = isPremium || isPaidFolder || isWatchToAccess;

  return (
    <Flex direction="column" gap={24}>
      <FolderDescription folder={selectedFolder} />
      {hasSubfolders && showSubFolders && (
        <BiLoggerContextProvider value={{ sectionname: 'subfolders' }}>
          <FolderListDesktop
            streamId={GALLERY_SUBFOLDERS_STREAM.streamId}
            selectedFolder={selectedFolder}
            measuredCookieType={MeasuredCookieType.STUDIO_SUBFOLDER_CARD}
            expandedByDefault={foldersExpandedByDefault}
            withCreateButton={!isSpecialFolder}
            withFolderActions={true}
            withDragToOrder={true}
            withHeader
          />
        </BiLoggerContextProvider>
      )}
      {/* Empty div to make flexbox happy */}
      {!hasSubfolders && hasDeviations && <div></div>}
      {hasSubfolders && hasDeviations && (
        <Heading className={cn(s['heading'], s['files-heading'])}>
          {t('studio.table.heading')}
        </Heading>
      )}
    </Flex>
  );
};
FolderHeaderDesktop.displayName = 'FolderHeaderDesktop';

export default FolderHeaderDesktop;
